import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import IconPlay from 'components/icons/IconPlay';
import IconPlus from 'components/icons/iconPlus';
import IconMinus from 'components/icons/iconMinus';
import IconClose from 'components/icons/iconClose';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import useVideoMetadata from 'hooks/useVideoMetadata';
import usePinchZoom from 'hooks/usePinchZoom';
import useIsHoverDevice from 'hooks/useIsHoverDevice';
import useTemporaryReveal from 'hooks/useTemporaryReveal';
import useScrollSelectedIntoView from 'hooks/useScrollSelectedIntoView';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import getMobileGallerySizes from './getMobileGallerySizes';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import PDPGalleryRevampCircleButton from '../PDPDesktopGalleryRevamp/PDPGalleryRevampCircleButton';
import PDPGalleryRevampLifestyleThumb from '../PDPDesktopGalleryRevamp/PDPGalleryRevampLifestyleThumb';
const MAX_ZOOM = 3;
const THUMB_CLASS = 'h-16 w-20 shrink-0 snap-start';

// Expanded lifestyle gallery (CON-8444 mobile): full-bleed photo + bottom thumb strip
const PDPMobileGalleryRevampExpandedGallery = ({
  enableCrops,
  initialIndex = 0,
  introVideoId,
  onClose,
  photos = [],
  productName,
  viewGalleryImageEvent
}) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [isVideoSelected, setIsVideoSelected] = useState(false);
  const photoCount = photos.length;
  const hasMultiplePhotos = photoCount > 1;
  const scrollerRef = useRef(null);
  const thumbStripRef = useRef(null);
  // Last index the scroller settled on, so analytics fires once per photo
  const settledIndexRef = useRef(initialIndex);
  // Photo picked while the video was showing, applied once the scroller is back
  const pendingIndexRef = useRef(null);

  // The video tile takes the first slot, so a photo's thumb sits one along
  useScrollSelectedIntoView({
    scrollerRef: thumbStripRef,
    selectedIndex: isVideoSelected ? 0 : selectedIndex + (introVideoId ? 1 : 0)
  });
  const isHoverDevice = useIsHoverDevice();

  // Kept in a ref so usePinchZoom's onTap can stay a stable identity
  const revealArrowsRef = useRef(null);
  const {
    canZoomIn,
    canZoomOut,
    containerRef,
    contentRef,
    isZoomed,
    reset: resetZoom,
    scaleStep,
    zoomIn,
    zoomOut
  } = usePinchZoom({
    maxScale: MAX_ZOOM,
    onTap: useCallback(() => revealArrowsRef.current?.(), [])
  });

  // A pointer device keeps the arrows up, since there is no tap to re-show them
  const {
    isRevealed: areArrowsRevealed,
    reveal: revealArrows
  } = useTemporaryReveal({
    held: isZoomed && !isHoverDevice
  });
  revealArrowsRef.current = revealArrows;
  const areArrowsVisible = isHoverDevice ? !isZoomed : areArrowsRevealed;
  const {
    thumbnail: videoThumbnailUrl
  } = useVideoMetadata({
    videoId: introVideoId
  });

  // JBImage needs explicit sizes inside the zoomed wrapper
  const mainPhotoSizes = getMobileGallerySizes(scaleStep);
  const basePhotoSizes = getMobileGallerySizes(1);

  // Fires once the scroller has settled on a photo, not on every scroll frame
  const reportPhotoView = useCallback(index => {
    const src = photos[index]?.src;
    viewGalleryImageEvent?.({
      imageLocation: 'Hero Gallery',
      imageFilename: src?.split('/').pop() || '',
      position: index,
      totalImages: photos.length
    });
  }, [photos, viewGalleryImageEvent]);

  // Scrolling is the source of truth: move the scroller and let its handler
  // update the index, so a thumb tap and a swipe end up in the same place
  const scrollToPhoto = useCallback((index, smooth = true) => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    scroller.scrollTo({
      left: index * scroller.clientWidth,
      behavior: smooth ? 'smooth' : 'auto'
    });
  }, []);
  const selectPhoto = index => {
    setIsVideoSelected(false);
    resetZoom();
    if (isVideoSelected) {
      // Nothing to scroll yet: the scroller is unmounted while the video shows
      pendingIndexRef.current = index;
      return;
    }
    scrollToPhoto(index);
  };

  // Stops at both ends. A snap scroller cannot wrap.
  const goToPrevious = useCallback(() => scrollToPhoto(Math.max(selectedIndex - 1, 0)), [scrollToPhoto, selectedIndex]);
  const goToNext = useCallback(() => scrollToPhoto(Math.min(selectedIndex + 1, photoCount - 1)), [photoCount, scrollToPhoto, selectedIndex]);

  // Jump to the thumbnail that was tapped, before the first paint so it never
  // reads as the gallery scrolling on open
  useLayoutEffect(() => {
    scrollToPhoto(initialIndex, false);
    // Only on mount: afterwards the scroller owns its own position
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The scroller remounts at its old position, so jump before the first paint
  useLayoutEffect(() => {
    if (isVideoSelected || pendingIndexRef.current == null) {
      return;
    }
    const index = pendingIndexRef.current;
    pendingIndexRef.current = null;
    scrollToPhoto(index, false);
    setSelectedIndex(index);
    settledIndexRef.current = index;
  }, [isVideoSelected, scrollToPhoto]);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return undefined;
    }
    let frame = null;
    const handleScroll = () => {
      if (frame) {
        return;
      }

      // Coalesced to one read per frame, since scroll fires far more often
      frame = requestAnimationFrame(() => {
        frame = null;
        const {
          clientWidth
        } = scroller;
        if (!clientWidth) {
          return;
        }
        const index = Math.round(scroller.scrollLeft / clientWidth);
        if (index !== settledIndexRef.current) {
          settledIndexRef.current = index;
          setSelectedIndex(index);
          reportPhotoView(index);
          revealArrowsRef.current?.();
        }
      });
    };
    scroller.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      scroller.removeEventListener('scroll', handleScroll);
    };
  }, [reportPhotoView]);
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="flex flex-col h-full" data-xc="PDPMobileGalleryRevampExpandedGallery">
      <div className="relative flex-1 min-h-0">
        {isVideoSelected && introVideoId ? <div className="h-full w-full bg-gray-light8">
            <PDPProductIntroVideo videoId={introVideoId} />
          </div> : <div ref={scrollerRef} className={classnames('h-full flex bg-gray-light8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
      // Zooming hands every gesture to usePinchZoom, so snapping and
      // horizontal scrolling both have to stop or they fight the pan
      isZoomed ? 'overflow-hidden' : 'overflow-x-auto overflow-y-hidden snap-x snap-mandatory')}>
            {photos.map((photo, index) => {
          const isActive = index === selectedIndex;
          return <div key={`PDPMobileGalleryRevampSlide${photo.id}`}
          // Zoom is mounted on the active slide only, so usePinchZoom's
          // refs follow the photo actually on screen
          ref={isActive ? containerRef : undefined}
          // pan-x keeps snap paging, pan-y keeps the page scrollable
          // behind the photo. Leaving pinch-zoom out of the list is
          // what stops the browser zooming the page.
          style={{
            touchAction: isZoomed ? 'none' : 'pan-x pan-y'
          }}
          // snap-always caps a gesture at one photo, otherwise a hard
          // flick skips several
          className="h-full w-full shrink-0 snap-center snap-always overflow-hidden">
                  <div
            // usePinchZoom writes transform straight to this node. Origin
            // is top-left so the pinch focal point maths stays simple
            ref={isActive ? contentRef : undefined} className={classnames('h-full w-full origin-top-left', {
              'will-change-transform': isActive
            })}>
                    <JBProductImage className="h-full w-full object-cover select-none pointer-events-none" alt={productName ? String(productName) : ''} {...getMainImageImgixPropsForPhoto(photo, enableCrops)} sizes={isActive ? mainPhotoSizes : basePhotoSizes} lazy={Math.abs(index - initialIndex) > 1} priority={index === initialIndex} />
                  </div>
                </div>;
        })}
          </div>}

        {/* Faded rather than unmounted, so they can come back without popping.
            Hidden while zoomed so they never sit on top of a panning gesture. */}
        {hasMultiplePhotos && !isVideoSelected && <div className={classnames('transition-opacity duration-[400ms] ease-out', areArrowsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none')} aria-hidden={!areArrowsVisible}>
            {selectedIndex > 0 && <div className="absolute left-4 top-1/2 -translate-y-1/2 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Previous image" onClick={goToPrevious}>
                  <IconArrowLeftWhite width={13} height={13} />
                </PDPGalleryRevampCircleButton>
              </div>}
            {/* left-4 / right-4 to share the close and zoom column's inset */}
            {selectedIndex < photoCount - 1 && <div className="absolute right-4 top-1/2 -translate-y-1/2 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Next image" onClick={goToNext}>
                  <IconArrowRightWhite width={13} height={13} />
                </PDPGalleryRevampCircleButton>
              </div>}
          </div>}

        {/* Same spot as the default state's circle-button row */}
        <div className="absolute flex flex-col gap-2.5 right-4 top-4 z-[2]">
          <PDPGalleryRevampCircleButton ariaLabel="Close gallery" onClick={onClose}>
            <IconClose width={9} height={9} />
          </PDPGalleryRevampCircleButton>
          {!isVideoSelected && <>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom in" disabled={!canZoomIn} onClick={zoomIn}>
                <IconPlus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom out" disabled={!canZoomOut} onClick={zoomOut}>
                <IconMinus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
            </>}
        </div>
      </div>

      {/* px so the first and last thumb can scroll clear of the edge instead of
          resting half-cut. snap keeps them landing on a whole thumbnail. */}
      <div ref={thumbStripRef} className="flex gap-1.5 overflow-x-auto snap-x px-2 py-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {!!introVideoId && <PDPGalleryRevampLifestyleThumb ariaLabel="Play product video" className={THUMB_CLASS} imgSize={{
        width: 160,
        height: 128
      }} src={videoThumbnailUrl} isSelected={isVideoSelected} onClick={() => {
        setIsVideoSelected(true);
        resetZoom();
      }} icon={<IconPlay width={20} height={20} />} />}
        {photos.map(({
        id,
        src
      }, index) => <PDPGalleryRevampLifestyleThumb key={`PDPMobileGalleryRevampExpandedThumb${id}`} className={THUMB_CLASS} imgSize={{
        width: 160,
        height: 128
      }} src={src} isSelected={!isVideoSelected && index === selectedIndex} onClick={() => selectPhoto(index)} />)}
      </div>
    </div>;
};
PDPMobileGalleryRevampExpandedGallery.propTypes = {
  enableCrops: PropTypes.bool,
  initialIndex: PropTypes.number,
  introVideoId: PropTypes.string,
  onClose: PropTypes.func,
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired
  })),
  productName: PropTypes.string,
  viewGalleryImageEvent: PropTypes.func
};
export default PDPMobileGalleryRevampExpandedGallery;